import { useMemo, useState, useRef, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MapPinIcon,
  ImageOffIcon,
} from 'lucide-react';
import {
  type SensorType,
  getSensorIcon,
  getSensorColor,
} from '../../utils/sensorUtils';
import {
  Point,
  getBoundingBox,
  getPolygonCentroid,
  pointsToSvgString,
  rectToPoints,
} from '../../utils/polygonUtils';
import { useZonesTelemetry } from '../../hooks/useZonesTelemetry';
import { getComfortState } from '../../utils/comfort';
import { getAQIGrade, overallAqiScore } from '../../utils/aqiUtils';
import { getEnergyGrade, whToKwh } from '../../utils/energyUtils';
import { ConnectedDeviceItem } from './ConnectedDeviceItem';
import { useConnectedDevices } from '../../hooks/useConnectedDevices';
import { useTranslation } from 'react-i18next';

export type { SensorType };
export type ViewMode = 'energy' | 'comfort' | 'aqi' | 'devices' | 'temperature' | 'airquality';

export interface Sensor {
  id: string;
  type: SensorType;
  label: string;
  alias?: string;
  roomId?: string;
  active: boolean;
  x: number;
  y: number;
  primary_appliance?: string;
}

interface Room {
  id: string;
  name: string;
  temp: number;
  humidity: number;
  energy: number;
  aqi: number;
  icon: any;
}

interface FloorPlanHeatMapProps {
  rooms: Room[];
  selectedRoomId: string;
  onSelectRoom: (id: string) => void;
  onSelectSensor?: (sensor: Sensor) => void;
  selectedSensorId?: string | null;
  viewMode?: ViewMode;
  zoneOverrides?: {
    id: string;
    name?: string;
    top: number;
    left: number;
    width: number;
    height: number;
    points?: Point[];
    sensors: Sensor[];
  }[];
  currentAssetId?: string | null;
  floorPlanUrl?: string | null;
  allSensors?: Sensor[];
  sensorTelemetry?: Record<string, any>;
}

const DEFAULT_COORDS = {
  top: '40%',
  left: '40%',
  width: '20%',
  height: '20%'
};

export function FloorPlanHeatMap({
  rooms,
  selectedRoomId,
  onSelectRoom,
  onSelectSensor,
  selectedSensorId,
  viewMode = 'energy',
  zoneOverrides,
  floorPlanUrl,
  currentAssetId,
  allSensors = [],
  sensorTelemetry = {}
}: FloorPlanHeatMapProps) {
  const { t } = useTranslation();
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  const [imageBounds, setImageBounds] = useState<{
    top: number;
    left: number;
    width: number;
    height: number;
  } | null>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const imageWrapperRef = useRef<HTMLDivElement>(null);

  const updateImageBounds = useCallback(() => {
    if (!imageRef.current || !imageWrapperRef.current) return;
    const img = imageRef.current;
    const wrapperW = imageWrapperRef.current.clientWidth;
    const wrapperH = imageWrapperRef.current.clientHeight;
    const naturalW = img.naturalWidth || 1;
    const naturalH = img.naturalHeight || 1;
    const aspectRatio = naturalW / naturalH;
    const containerAspect = wrapperW / wrapperH;
    let renderedW: number, renderedH: number;
    if (aspectRatio > containerAspect) {
      renderedW = wrapperW;
      renderedH = wrapperW / aspectRatio;
    } else {
      renderedH = wrapperH;
      renderedW = wrapperH * aspectRatio;
    }
    setImageBounds({
      top: (wrapperH - renderedH) / 2,
      left: (wrapperW - renderedW) / 2,
      width: renderedW,
      height: renderedH
    });
  }, []);

  useEffect(() => {
    const observer = new ResizeObserver(() => updateImageBounds());
    if (imageWrapperRef.current) observer.observe(imageWrapperRef.current);
    return () => observer.disconnect();
  }, [updateImageBounds]);

  const zoneIds = useMemo(() =>
    (zoneOverrides ?? []).map(z => z.id),
  [zoneOverrides]);

  const { zonesData } = useZonesTelemetry(
    zoneIds,
    ['temperature', 'humidity', 'CO2', 'TVOCs', 'dust', 'calculated_24h_power', 'comfort', 'presence_state', 'light_state']
  );

  const { devices: connectedDevices } = useConnectedDevices(currentAssetId);

  const roomPolygonMap = useMemo(() => {
    const map: Record<
      string,
      {
        points: Point[];
        centroid: Point;
        bbox: { top: number; left: number; width: number; height: number };
        pointsStr: string;
      }
    > = {};

    if (zoneOverrides) {
      zoneOverrides.forEach((z) => {
        const points =
          z.points && z.points.length >= 3
            ? z.points
            : rectToPoints(z.top || 0, z.left || 0, z.width || 20, z.height || 20);
        const centroid = getPolygonCentroid(points);
        const bbox = getBoundingBox(points);
        map[z.id] = {
          points,
          centroid,
          bbox,
          pointsStr: pointsToSvgString(points),
        };
      });
    }
    return map;
  }, [zoneOverrides]);

  // Calculate absolute coordinates for each SEL light based on its parent Zone's bounding box & relative (x, y)
  const allLightsWithCoords = useMemo(() => {
    const list: {
      sensor: Sensor;
      absX: number;
      absY: number;
      zoneName?: string;
    }[] = [];
    const addedIds = new Set<string>();

    // 1) Iterate over all zoneOverrides and their assigned sensors
    if (zoneOverrides) {
      zoneOverrides.forEach((zone) => {
        const bbox =
          zone.points && zone.points.length >= 3
            ? getBoundingBox(zone.points)
            : { left: zone.left || 0, top: zone.top || 0, width: zone.width || 20, height: zone.height || 20 };

        (zone.sensors || []).forEach((sensor) => {
          if (sensor.type === 'SEL' || sensor.type === 'LIGHT' || sensor.type === 'LIGHTING') {
            // sensor.x & sensor.y are relative % (0~100) inside the zone!
            const relX = sensor.x !== undefined && sensor.x !== null ? Number(sensor.x) : 50;
            const relY = sensor.y !== undefined && sensor.y !== null ? Number(sensor.y) : 50;

            const absX = bbox.left + (relX / 100) * bbox.width;
            const absY = bbox.top + (relY / 100) * bbox.height;

            list.push({
              sensor,
              absX,
              absY,
              zoneName: zone.name
            });
            addedIds.add(sensor.id);
          }
        });
      });
    }

    // 2) Check any unmapped allSensors that have a roomId
    allSensors.forEach((sensor) => {
      if ((sensor.type === 'SEL' || sensor.type === 'LIGHT' || sensor.type === 'LIGHTING') && !addedIds.has(sensor.id)) {
        let absX = 50;
        let absY = 50;
        let zoneName = '';

        if (sensor.roomId && roomPolygonMap[sensor.roomId]) {
          const poly = roomPolygonMap[sensor.roomId];
          const relX = sensor.x !== undefined && sensor.x !== null ? Number(sensor.x) : 50;
          const relY = sensor.y !== undefined && sensor.y !== null ? Number(sensor.y) : 50;
          absX = poly.bbox.left + (relX / 100) * poly.bbox.width;
          absY = poly.bbox.top + (relY / 100) * poly.bbox.height;
          zoneName = zoneOverrides?.find(z => z.id === sensor.roomId)?.name || '';
        }

        list.push({
          sensor,
          absX,
          absY,
          zoneName
        });
        addedIds.add(sensor.id);
      }
    });

    return list;
  }, [zoneOverrides, allSensors, roomPolygonMap]);



  const effectiveCoords: Record<
    string,
    { top: string; left: string; width: string; height: string }
  > = useMemo(() => {
    const base: Record<string, { top: string; left: string; width: string; height: string }> = {};
    if (zoneOverrides) {
      zoneOverrides.forEach((z) => {
        base[z.id] = {
          top: `${z.top}%`,
          left: `${z.left}%`,
          width: `${z.width}%`,
          height: `${z.height}%`
        };
      });
    }
    return base;
  }, [zoneOverrides]);

  const getAqiColor = (score: number) => {
    if (score >= 80)
      return {
        bg: 'bg-emerald-500/30',
        border: 'border-emerald-400/50',
        text: 'text-emerald-200'
      };
    if (score >= 60)
      return {
        bg: 'bg-cyan-500/30',
        border: 'border-cyan-400/50',
        text: 'text-cyan-200'
      };
    if (score >= 40)
      return {
        bg: 'bg-amber-500/30',
        border: 'border-amber-400/50',
        text: 'text-amber-200'
      };
    return {
      bg: 'bg-red-500/30',
      border: 'border-red-400/50',
      text: 'text-red-200'
    };
  };

  const getSvgColors = (bgClass: string, isSelected: boolean) => {
    if (isSelected) {
      return {
        fill: 'rgba(34, 211, 238, 0.40)',
        stroke: '#22d3ee',
        glow: 'drop-shadow(0 0 12px rgba(34, 211, 238, 0.8))',
      };
    }
    if (!bgClass || bgClass.includes('transparent')) {
      return { fill: 'transparent', stroke: 'transparent', glow: 'none' };
    }
    if (bgClass.includes('emerald') || bgClass.includes('green')) {
      return { fill: 'rgba(16, 185, 129, 0.35)', stroke: 'rgba(52, 211, 153, 0.8)', glow: 'none' };
    }
    if (bgClass.includes('cyan')) {
      return { fill: 'rgba(6, 182, 212, 0.35)', stroke: 'rgba(34, 211, 238, 0.8)', glow: 'none' };
    }
    if (bgClass.includes('blue')) {
      return { fill: 'rgba(59, 130, 246, 0.35)', stroke: 'rgba(96, 165, 250, 0.8)', glow: 'none' };
    }
    if (bgClass.includes('yellow')) {
      return { fill: 'rgba(234, 179, 8, 0.35)', stroke: 'rgba(250, 204, 21, 0.8)', glow: 'none' };
    }
    if (bgClass.includes('amber')) {
      return { fill: 'rgba(245, 158, 11, 0.35)', stroke: 'rgba(251, 191, 36, 0.8)', glow: 'none' };
    }
    if (bgClass.includes('orange')) {
      return { fill: 'rgba(249, 115, 22, 0.35)', stroke: 'rgba(251, 146, 60, 0.8)', glow: 'none' };
    }
    if (bgClass.includes('red')) {
      return { fill: 'rgba(239, 68, 68, 0.35)', stroke: 'rgba(248, 113, 113, 0.8)', glow: 'none' };
    }
    if (bgClass.includes('slate') || bgClass.includes('gray')) {
      return { fill: 'rgba(100, 116, 139, 0.35)', stroke: 'rgba(148, 163, 184, 0.8)', glow: 'none' };
    }
    return { fill: 'rgba(6, 182, 212, 0.35)', stroke: 'rgba(34, 211, 238, 0.8)', glow: 'none' };
  };

  const getRoomColors = (room: Room) => {
    const data = zonesData[room.id] || {};

    switch (viewMode) {
      case 'comfort':
      case 'temperature': {
        const tempC = data['temperature']?.[0]?.value ? Number(data['temperature'][0].value) : room.temp;
        const humC = data['humidity']?.[0]?.value ? Number(data['humidity'][0].value) : 50;
        return getComfortState(tempC, humC);
      }
      case 'aqi':
      case 'airquality': {
        const co2 = data['CO2']?.[0]?.value ? Number(data['CO2'][0].value) : 400;
        const tvoc = data['TVOCs']?.[0]?.value ? Number(data['TVOCs'][0].value) : 0.1;
        const pm25 = data['dust']?.[0]?.value ? Number(data['dust'][0].value) : 10;
        const aqiScore = overallAqiScore({ co2, tvoc, pm25 });
        return getAqiColor(aqiScore);
      }
      case 'devices':
        return {
          bg: 'bg-transparent',
          border: 'border-transparent',
          text: 'text-transparent'
        };
      case 'energy':
      default: {
        const rawPower = data['calculated_24h_power']?.[0]?.value ?? room.energy ?? 0;
        const powerKwh = whToKwh(rawPower);
        const energyState = getEnergyGrade(powerKwh);
        return {
          bg: energyState.bgColor.replace('/20', '/30'),
          border: `border-${energyState.color.split('-')[1]}-${energyState.color.split('-')[2]}/50`,
          text: energyState.color.replace('400', '200')
        };
      }
    }
  };

  const getRoomDisplayValue = (room: Room) => {
    const data = zonesData[room.id] || {};

    switch (viewMode) {
      case 'comfort':
      case 'temperature': {
        const temp = data['temperature']?.[0]?.value ? Number(data['temperature'][0].value).toFixed(1) : room.temp.toFixed(1);
        const hum = data['humidity']?.[0]?.value ? Number(data['humidity'][0].value).toFixed(0) : room.humidity.toFixed(0);
        return `${temp}°C · ${hum}%`;
      }
      case 'aqi':
      case 'airquality': {
        const co2 = data['CO2']?.[0]?.value ? Number(data['CO2'][0].value) : 400;
        const tvoc = data['TVOCs']?.[0]?.value ? Number(data['TVOCs'][0].value) : 0.1;
        const pm25 = data['dust']?.[0]?.value ? Number(data['dust'][0].value) : 10;
        return `${overallAqiScore({ co2, tvoc, pm25 })}점`;
      }
      case 'devices':
        return '';
      case 'energy':
      default: {
        const rawPower = data['calculated_24h_power']?.[0]?.value ?? room.energy ?? 0;
        const powerKwh = whToKwh(rawPower);
        return `${powerKwh.toFixed(3)} kWh`;
      }
    }
  };

  const getLegendTitle = () => {
    switch (viewMode) {
      case 'energy': return t('legend.energy_consumption');
      case 'comfort': return t('legend.comfort_status');
      case 'aqi':
      case 'airquality': return t('legend.aqi_grade', '공기질');
      case 'temperature': return t('legend.temperature_status');
      default: return t('legend.title');
    }
  };

  const getLegendItems = () => {
    switch (viewMode) {
      case 'energy':
        return [
          { label: t('legend.low'), color: 'bg-emerald-500' },
          { label: t('legend.normal'), color: 'bg-cyan-500' },
          { label: t('legend.high'), color: 'bg-amber-500' },
          { label: t('legend.very_high'), color: 'bg-red-500' }
        ];
      case 'comfort':
        return [
          { label: t('legend.pleasant'), color: 'bg-emerald-500' },
          { label: t('legend.fair'), color: 'bg-cyan-500' },
          { label: t('legend.moderate'), color: 'bg-amber-500' },
          { label: t('legend.unpleasant'), color: 'bg-red-500' }
        ];
      case 'aqi':
      case 'airquality':
        return [
          { label: t('legend.good'), color: 'bg-emerald-500' },
          { label: t('legend.normal'), color: 'bg-cyan-500' },
          { label: t('legend.bad'), color: 'bg-amber-500' },
          { label: t('legend.very_bad'), color: 'bg-red-500' }
        ];
      case 'temperature':
        return [
          { label: `${t('legend.cool')} (<22°C)`, color: 'bg-blue-500' },
          { label: `${t('legend.optimal')} (22~26°C)`, color: 'bg-emerald-500' },
          { label: `${t('legend.warm')} (>26°C)`, color: 'bg-red-500' }
        ];
      default:
        return [];
    }
  };

  const resolvedRooms = rooms.filter(r => r.id !== 'ALL');
  const targetFloorPlanUrl = floorPlanUrl || '/floorplan.svg';

  return (
    <div className="relative w-full h-full flex flex-col items-center justify-center px-2 py-1 select-none overflow-hidden">
      {/* Main Floor Plan Container (Same full-size layout as original) */}
      <motion.div
        className="relative w-full h-full transition-transform duration-500 ease-in-out"
        style={{
          maxWidth: 1600,
          transformOrigin: 'center center'
        }}
      >
        <div ref={imageWrapperRef} className="relative w-full h-full">
          {targetFloorPlanUrl ? (
            <img
              ref={imageRef}
              src={targetFloorPlanUrl}
              alt="Floor Plan"
              onLoad={() => {
                setImageLoaded(true);
                updateImageBounds();
              }}
              onError={() => setImageError(true)}
              className={`absolute inset-0 w-full h-full object-contain opacity-80 drop-shadow-2xl transition-opacity duration-500 ${
                imageLoaded ? 'opacity-80' : 'opacity-0'
              }`}
              crossOrigin="anonymous"
            />
          ) : null}

          {!imageLoaded && !imageError && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="flex flex-col items-center gap-3 text-slate-500">
                <div className="w-8 h-8 border-2 border-cyan-400/50 border-t-cyan-400 rounded-full animate-spin" />
                <span className="text-xs font-mono uppercase tracking-wider">
                  공간 맵 로딩 중...
                </span>
              </div>
            </div>
          )}

          {imageError && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="flex flex-col items-center gap-3 text-slate-500">
                <ImageOffIcon className="w-10 h-10 text-slate-600" />
                <span className="text-xs font-mono uppercase tracking-wider">
                  공간 맵이 없습니다.
                </span>
              </div>
            </div>
          )}

          {/* SEL Lights overlay - in devices mode */}
          {imageLoaded && imageBounds && viewMode === 'devices' && (
            <div
              className="absolute z-30 pointer-events-none"
              style={{
                top: `${imageBounds.top}px`,
                left: `${imageBounds.left}px`,
                width: `${imageBounds.width}px`,
                height: `${imageBounds.height}px`
              }}
            >
              {allLightsWithCoords.map(({ sensor, absX, absY, zoneName }) => {
                // Determine ON / OFF state from telemetry
                const data = sensorTelemetry[sensor.id];
                const lightState = data?.light_state?.[0]?.value;
                const power = data?.power?.[0]?.value;
                const state = data?.state?.[0]?.value;
                const sw = data?.switch?.[0]?.value;

                const isOn =
                  lightState === '1' ||
                  lightState === true ||
                  lightState === 1 ||
                  power === 1 ||
                  power === '1' ||
                  state === 'ON' ||
                  state === 'on' ||
                  sw === true ||
                  sw === 'true';

                return (
                  <div
                    key={sensor.id}
                    className="absolute pointer-events-auto cursor-pointer -translate-x-1/2 -translate-y-1/2 group flex items-center justify-center"
                    style={{
                      left: `${absX}%`,
                      top: `${absY}%`,
                      width: '48px',
                      height: '48px'
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (onSelectSensor) onSelectSensor(sensor);
                    }}
                  >
                    {/* Radiating Light Glow when ON */}
                    {isOn && (
                      <>
                        {/* Outermost soft light diffusion halo */}
                        <motion.div
                          initial={{ scale: 0.8, opacity: 0 }}
                          animate={{ scale: [1, 1.25, 1], opacity: [0.6, 0.9, 0.6] }}
                          transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
                          className="absolute inset-0 rounded-full pointer-events-none"
                          style={{
                            background: 'radial-gradient(circle, rgba(253, 224, 71, 0.45) 0%, rgba(251, 191, 36, 0.22) 40%, rgba(245, 158, 11, 0.06) 70%, transparent 85%)',
                            filter: 'blur(2px)'
                          }}
                        />
                        {/* Inner radiant glow ring */}
                        <div
                          className="absolute w-7 h-7 rounded-full pointer-events-none animate-pulse"
                          style={{
                            background: 'radial-gradient(circle, rgba(255, 255, 255, 0.9) 0%, rgba(253, 224, 71, 0.6) 50%, transparent 80%)'
                          }}
                        />
                      </>
                    )}

                    {/* Small Round Lamp Bulb */}
                    <motion.div
                      whileHover={{ scale: 1.3 }}
                      whileTap={{ scale: 0.9 }}
                      className={`relative z-10 w-3.5 h-3.5 rounded-full transition-all duration-300 shadow-md ${
                        isOn
                          ? 'bg-amber-300 ring-2 ring-amber-400/80 shadow-[0_0_10px_#fde047]'
                          : 'bg-slate-300 border border-slate-400 shadow-sm'
                      }`}
                    />

                    {/* Tooltip on hover */}
                    <div className="absolute bottom-full mb-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-40 whitespace-nowrap px-2 py-0.5 rounded-md bg-slate-900/90 border border-white/20 text-[0.65rem] font-medium text-slate-200 shadow-xl backdrop-blur-sm">
                      {sensor.label || sensor.alias || '조명'} {zoneName ? `(${zoneName})` : ''} - {isOn ? '켜짐' : '꺼짐'}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Zones Overlay Layer */}
          {imageLoaded && imageBounds && (
            <div
              className="absolute"
              style={{
                top: `${imageBounds.top}px`,
                left: `${imageBounds.left}px`,
                width: `${imageBounds.width}px`,
                height: `${imageBounds.height}px`,
              }}
            >
              {/* SVG Polygon Overlay Layer */}
              <svg
                className="absolute inset-0 w-full h-full pointer-events-none overflow-visible"
                viewBox="0 0 100 100"
                preserveAspectRatio="none"
              >
                <defs>
                  {/* Dark gradient for rooms where all lights are OFF */}
                  <radialGradient id="zone-lights-off-dark-gradient" cx="50%" cy="50%" r="60%">
                    <stop offset="0%" stopColor="#020617" stopOpacity="0.82" />
                    <stop offset="70%" stopColor="#0f172a" stopOpacity="0.75" />
                    <stop offset="100%" stopColor="#000000" stopOpacity="0.90" />
                  </radialGradient>
                </defs>

                {resolvedRooms
                  .filter((r) => r.id !== 'ALL')
                  .map((room) => {
                    const polyInfo = roomPolygonMap[room.id];
                    if (!polyInfo) return null;

                    const isSelected = selectedRoomId === room.id;

                    // In devices mode: Darken zone if all lights in this zone are OFF
                    if (viewMode === 'devices') {
                      const zoneOverride = zoneOverrides?.find(z => z.id === room.id);
                      const zoneLights = (zoneOverride?.sensors || allSensors.filter(s => s.roomId === room.id))
                        .filter(s => s.type === 'SEL' || s.type === 'LIGHT' || s.type === 'LIGHTING');
                      
                      const hasLights = zoneLights.length > 0;
                      const hasAnyLightOn = zoneLights.some(sensor => {
                        const data = sensorTelemetry[sensor.id];
                        const lightState = data?.light_state?.[0]?.value;
                        const power = data?.power?.[0]?.value;
                        const state = data?.state?.[0]?.value;
                        const sw = data?.switch?.[0]?.value;
                        return lightState === '1' || lightState === true || lightState === 1 ||
                               power === 1 || power === '1' ||
                               state === 'ON' || state === 'on' ||
                               sw === true || sw === 'true';
                      });

                      // If all lights are off (or no lights on in this zone)
                      const isDarkened = hasLights && !hasAnyLightOn;

                      return (
                        <polygon
                          key={`poly-${room.id}-${viewMode}`}
                          points={polyInfo.pointsStr}
                          onClick={(e) => {
                            e.stopPropagation();
                            onSelectRoom(room.id);
                          }}
                          fill={isDarkened ? 'url(#zone-lights-off-dark-gradient)' : 'rgba(251, 191, 36, 0.04)'}
                          stroke={isDarkened ? 'rgba(0, 0, 0, 0.6)' : 'rgba(251, 191, 36, 0.15)'}
                          strokeWidth={isSelected ? '0.75' : '0.35'}
                          className="pointer-events-auto cursor-pointer transition-all duration-300"
                        />
                      );
                    }

                    const colors = getRoomColors(room);
                    const svgColors = getSvgColors(colors.bg, isSelected);

                    return (
                      <polygon
                        key={`poly-${room.id}-${viewMode}`}
                        points={polyInfo.pointsStr}
                        onClick={(e) => {
                          e.stopPropagation();
                          onSelectRoom(room.id);
                        }}
                        fill={svgColors.fill}
                        stroke={svgColors.stroke}
                        strokeWidth={isSelected ? '0.75' : '0.35'}
                        className="pointer-events-auto cursor-pointer transition-all duration-300 hover:brightness-125"
                        style={{
                          opacity: 1,
                          filter: svgColors.glow,
                        }}
                      />
                    );
                  })}
              </svg>

              {/* Room Centroid Badges (Hidden in devices mode) */}
              {viewMode !== 'devices' && resolvedRooms
                .filter((r) => r.id !== 'ALL')
                .map((room) => {
                  const polyInfo = roomPolygonMap[room.id];
                  if (!polyInfo) return null;

                  const isSelected = selectedRoomId === room.id;
                  const colors = getRoomColors(room);

                  return (
                    <div
                      key={`room-overlay-${room.id}-${viewMode}`}
                      className="absolute pointer-events-auto"
                      style={{
                        left: `${polyInfo.centroid.x}%`,
                        top: `${polyInfo.centroid.y}%`,
                        transform: 'translate(-50%, -50%)',
                        zIndex: isSelected ? 25 : 10,
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelectRoom(room.id);
                      }}
                    >
                      <motion.div
                        whileHover={{ scale: 1.08 }}
                        className="relative flex flex-col items-center justify-center cursor-pointer text-center select-none"
                      >
                        <div className="flex items-center gap-1.5 drop-shadow-[0_2px_4px_rgba(0,0,0,0.9)]">
                          <span
                            className={`text-[0.75rem] font-roboto font-bold uppercase tracking-wider leading-tight drop-shadow-[0_2px_4px_rgba(0,0,0,0.9)] ${
                              isSelected ? 'text-cyan-300' : 'text-white'
                            }`}
                          >
                            {room.name}
                          </span>
                          <div
                            className={`w-1.5 h-1.5 rounded-full ${
                              isSelected
                                ? 'bg-cyan-400 shadow-[0_0_6px_#22d3ee]'
                                : colors.text.replace('text-', 'bg-')
                            }`}
                          />
                        </div>

                        {getRoomDisplayValue(room) ? (
                          <span className={`text-[0.72rem] font-roboto font-bold ${colors.text} drop-shadow-[0_2px_4px_rgba(0,0,0,0.9)] mt-0.5 leading-tight`}>
                            {getRoomDisplayValue(room)}
                          </span>
                        ) : null}
                      </motion.div>
                    </div>
                  );
                })}
            </div>
          )}
        </div>
      </motion.div>

      {/* Legend (Original position and style) */}
      {imageLoaded && viewMode !== 'devices' && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative z-40 backdrop-blur-lg px-4 py-3 rounded-2xl border flex flex-wrap items-center justify-center gap-x-4 gap-y-2 shadow-xl mt-4 mb-4 mx-4 w-[calc(100%-32px)]"
          style={{
            background: 'var(--mobile-bg-card)',
            borderColor: 'var(--mobile-border)',
            color: 'var(--mobile-text-secondary)'
          }}
        >
          <MapPinIcon className="w-3.5 h-3.5 text-cyan-500 flex-shrink-0" />
          <span className="text-[0.75rem] font-mono uppercase tracking-wider font-bold" style={{ color: 'var(--mobile-text-primary)' }}>
            {getLegendTitle()}
          </span>
          <span style={{ color: 'var(--mobile-border-strong)' }}>|</span>
          <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1">
            {getLegendItems().map((item) => (
              <span
                key={item.label}
                className="text-[0.7rem] font-mono font-bold flex items-center gap-1.5 whitespace-nowrap"
                style={{ color: 'var(--mobile-text-secondary)' }}
              >
                <span className={`w-2 h-2 rounded-full ${item.color} shadow-sm shadow-black/20`} />
                {item.label}
              </span>
            ))}
          </div>
        </motion.div>
      )}
    </div>
  );
}
